import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Eye, EyeOff, User, Mail, MapPin } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/api';
import { Button, Alert } from '../../components/common';
import { RegisterData } from '../../types';
import toast from 'react-hot-toast';

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterData & { confirmPassword: string }>();

  const password = watch('password');

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const onSubmit = async (data: RegisterData & { confirmPassword: string }) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const { confirmPassword, ...registerData } = data;

      await authService.register(registerData);
      
      toast.success('¡Registro exitoso! Ya puedes iniciar sesión.');
      navigate('/login');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Error en el registro';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brinsa-celeste/10">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brinsa-azul mx-auto"></div>
          <p className="mt-4 text-brinsa-azulOscuro/80">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Panel izquierdo con branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-brinsa-azul to-brinsa-azulOscuro relative overflow-hidden">
        <div className="absolute inset-0 bg-brinsa-azulOscuro/10" />
        <div className="relative z-10 flex flex-col justify-center px-12 text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <img
              src="/logo-white.png"
              alt="Brinsa"
              className="h-16 w-auto mb-8"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden">
              <h1 className="text-4xl font-bold mb-4">Brinsa</h1>
            </div>
            
            <h2 className="text-3xl font-bold mb-4">
              ¡Bienvenido!
            </h2>
            <p className="text-xl mb-8 opacity-90">
              Ingresa tus datos para registrarte.
            </p>
            
            <div className="bg-white/10 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">¡Te damos la bienvenida al Campus Virtual Brinsa!</h3>
              <p className="text-sm opacity-90">
                Únete a nuestra plataforma educativa y accede a contenido interactivo, 
                realidad aumentada y certificaciones que te ayudarán a crecer profesionalmente.
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Panel derecho con formulario */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="mx-auto w-full max-w-md"
        >
          {/* Logo móvil */}
          <div className="lg:hidden text-center mb-8">
            <img
              src="/logo.png"
              alt="Brinsa"
              className="h-12 w-auto mx-auto mb-4"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="hidden">
              <h1 className="text-2xl font-bold text-brinsa-azul">Brinsa</h1>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-brinsa-azul text-center">
              ¡Bienvenido!
            </h2>
            <p className="mt-2 text-center text-brinsa-azulOscuro/80">
              Ingresa tus datos para registrarte.
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            {error && (
              <Alert
                type="error"
                message={error}
                onClose={() => setError(null)}
              />
            )}

            <div className="space-y-4">
              {/* Campo de nombre */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-brinsa-azul">
                  Nombre
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('name', {
                      required: 'El nombre es requerido',
                      minLength: {
                        value: 2,
                        message: 'El nombre debe tener al menos 2 caracteres'
                      }
                    })}
                    type="text"
                    className="appearance-none relative block w-full px-3 py-3 pl-10 border border-slate-300 placeholder-slate-500 text-brinsa-azul rounded-lg focus:outline-none focus:ring-brinsa-celeste focus:border-brinsa-celeste focus:z-10 text-base"
                    placeholder="Usuario Ejemplo"
                  />
                  <User className="absolute left-3 top-3.5 h-5 w-5 text-brinsa-celeste" />
                  {errors.name && (
                    <p className="mt-1 text-sm text-brinsa-rojo">{errors.name.message}</p>
                  )}
                </div>
              </div>

              {/* Campo de cédula */}
              <div>
                <label htmlFor="cedula" className="block text-sm font-medium text-brinsa-azul">
                  Número de cédula
                  <span className="text-brinsa-azulOscuro/70 text-xs"> (sin espacios ni puntos)</span>
                </label>
                <div className="mt-1">
                  <input
                    {...register('cedula', {
                      required: 'La cédula es requerida',
                      minLength: {
                        value: 6,
                        message: 'La cédula debe tener al menos 6 caracteres'
                      },
                      pattern: {
                        value: /^\d+$/,
                        message: 'La cédula solo debe contener números'
                      }
                    })}
                    type="text"
                    className="appearance-none relative block w-full px-3 py-3 border border-slate-300 placeholder-slate-500 text-brinsa-azul rounded-lg focus:outline-none focus:ring-brinsa-celeste focus:border-brinsa-celeste focus:z-10 text-base"
                    placeholder="00000000"
                  />
                  {errors.cedula && (
                    <p className="mt-1 text-sm text-brinsa-rojo">{errors.cedula.message}</p>
                  )}
                </div>
              </div>

              {/* Campo de email (opcional) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-brinsa-azul">
                  Email <span className="text-brinsa-azulOscuro/70">(opcional)</span>
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('email', {
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Email inválido'
                      }
                    })}
                    type="email"
                    className="appearance-none relative block w-full px-3 py-3 pl-10 border border-slate-300 placeholder-slate-500 text-brinsa-azul rounded-lg focus:outline-none focus:ring-brinsa-celeste focus:border-brinsa-celeste focus:z-10 text-base"
                    placeholder="usuario@ejemplo.com"
                  />
                  <Mail className="absolute left-3 top-3.5 h-5 w-5 text-brinsa-celeste" />
                  {errors.email && (
                    <p className="mt-1 text-sm text-brinsa-rojo">{errors.email.message}</p>
                  )}
                </div>
              </div>

              {/* Campo de zona geográfica */}
              <div>
                <label htmlFor="geographicZone" className="block text-sm font-medium text-brinsa-azul">
                  Zona geográfica <span className="text-brinsa-azulOscuro/70">(opcional)</span>
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('geographicZone')}
                    type="text"
                    className="appearance-none relative block w-full px-3 py-3 pl-10 border border-slate-300 placeholder-slate-500 text-brinsa-azul rounded-lg focus:outline-none focus:ring-brinsa-celeste focus:border-brinsa-celeste focus:z-10 text-base"
                    placeholder="Bogotá, Medellín, Cali..."
                  />
                  <MapPin className="absolute left-3 top-3.5 h-5 w-5 text-brinsa-celeste" />
                </div>
              </div>

              {/* Campo de contraseña */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-brinsa-azul">
                  Contraseña
                  <span className="text-brinsa-azulOscuro/70 text-xs"> (número de cédula)</span>
                </label>
                <div className="mt-1 relative">
                  <input
                    {...register('password', {
                      required: 'La contraseña es requerida',
                      minLength: {
                        value: 6,
                        message: 'La contraseña debe tener al menos 6 caracteres'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="appearance-none relative block w-full px-3 py-3 pr-10 border border-slate-300 placeholder-slate-500 text-brinsa-azul rounded-lg focus:outline-none focus:ring-brinsa-celeste focus:border-brinsa-celeste focus:z-10 text-base"
                    placeholder="••••••••••••"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-slate-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-slate-400" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="mt-1 text-sm text-brinsa-rojo">{errors.password.message}</p>
                  )}
                </div>
              </div>

              {/* Campo de confirmar contraseña */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-brinsa-azul">
                  Confirmar contraseña
                </label>
                <div className="mt-1">
                  <input
                    {...register('confirmPassword', {
                      required: 'Confirma tu contraseña',
                      validate: (value) =>
                        value === password || 'Las contraseñas no coinciden'
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="appearance-none relative block w-full px-3 py-3 border border-slate-300 placeholder-slate-500 text-brinsa-azul rounded-lg focus:outline-none focus:ring-brinsa-celeste focus:border-brinsa-celeste focus:z-10 text-base"
                    placeholder="••••••••••••"
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-brinsa-rojo">{errors.confirmPassword.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Botón de submit */}
            <Button
              type="submit"
              className="w-full justify-center"
              size="lg"
              isLoading={isSubmitting}
              variant="secondary"
            >
              REGISTRARSE
            </Button>

            {/* Enlace de login */}
            <div className="text-center">
              <span className="text-brinsa-azulOscuro/80">¿YA TIENES CUENTA? </span>
              <Link
                to="/login"
                className="font-medium text-brinsa-azul hover:text-brinsa-azulClaro transition-colors"
              >
                INICIA SESIÓN
              </Link>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
